import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";
import { triggerBlobDownload } from "@/lib/utils";

/**
 * Master v5 Prompt 8A — TanStack Query hooks for the block-based
 * WorkDocument engine. Mirrors presentationQueries.js exactly: `request()`
 * throws a structured error (`.status`, `.body`) so a 409 stale-block
 * conflict or a 409 "block_locked" can be distinguished by callers.
 */
async function request(path, options = {}) {
  const res = await rrFetch(path, options);
  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((body && (body.detail?.error || body.detail?.message || body.detail)) || "request_failed");
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const jsonPost = (path, payload) => request(path, { method: "POST", body: JSON.stringify(payload ?? {}) });
const jsonPatch = (path, payload) => request(path, { method: "PATCH", body: JSON.stringify(payload) });

export const wdqk = {
  list: (wrkId) => ["work-documents", wrkId],
  detail: (wrkId, docId) => ["work-document", wrkId, docId],
  versions: (wrkId, docId) => ["work-document-versions", wrkId, docId],
};

export function useWorkDocumentsList(workspaceId) {
  return useQuery({
    queryKey: wdqk.list(workspaceId),
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/work-documents`),
  });
}

export function useWorkDocumentDetail(workspaceId, docId) {
  return useQuery({
    queryKey: wdqk.detail(workspaceId, docId),
    enabled: !!workspaceId && !!docId,
    retry: false,
    refetchInterval: 8000, // polling fallback layer — SSE is the primary transport (see useBlockPresence)
    queryFn: () => request(`/api/rehearsal-room/work-documents/${docId}?wrk_id=${workspaceId}`),
  });
}

export function useWorkDocumentVersions(workspaceId, docId) {
  return useQuery({
    queryKey: wdqk.versions(workspaceId, docId),
    enabled: !!workspaceId && !!docId,
    queryFn: () => request(`/api/rehearsal-room/work-documents/${docId}/versions?wrk_id=${workspaceId}`),
  });
}

export function useCreateWorkDocument(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/work-documents`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: wdqk.list(workspaceId) }),
  });
}

export function useCreateBlock(workspaceId, docId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/work-documents/${docId}/blocks?wrk_id=${workspaceId}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: wdqk.detail(workspaceId, docId) }),
  });
}

export function useSubmitDocument(workspaceId, docId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => jsonPost(`/api/rehearsal-room/work-documents/${docId}/submit?wrk_id=${workspaceId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: wdqk.detail(workspaceId, docId) }),
  });
}

export function useReopenDocument(workspaceId, docId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => jsonPost(`/api/rehearsal-room/work-documents/${docId}/reopen?wrk_id=${workspaceId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: wdqk.detail(workspaceId, docId) }),
  });
}

// Master v5 Prompt 18 — the ONE explicit, separate, audited action that
// may force-release someone else's active lease. Coach-only.
export function useAdminReleaseLease(workspaceId, docId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ blockId, reason }) => jsonPost(`/api/rehearsal-room/work-documents/${docId}/blocks/${blockId}/admin-lease-release?wrk_id=${workspaceId}`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: wdqk.detail(workspaceId, docId) }),
  });
}

export function useSaveDocumentVersion(workspaceId, docId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (label) => jsonPost(`/api/rehearsal-room/work-documents/${docId}/versions?wrk_id=${workspaceId}`, { label }),
    onSuccess: () => qc.invalidateQueries({ queryKey: wdqk.versions(workspaceId, docId) }),
  });
}

export function useRestoreDocumentVersion(workspaceId, docId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (versionId) => jsonPost(`/api/rehearsal-room/work-documents/${docId}/versions/${versionId}/restore?wrk_id=${workspaceId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: wdqk.detail(workspaceId, docId) });
      qc.invalidateQueries({ queryKey: wdqk.versions(workspaceId, docId) });
    },
  });
}

// Master v5 Prompt 30F — reconstructs the coach's original uploaded
// file with this saved version's answers filled directly into it (same
// fonts/colours/images/layout) and downloads it as a PDF. A GET that
// returns a binary body, not JSON, so it bypasses request()/rrFetch's
// json parsing and drives the browser's native download via a blob URL.
export async function downloadVersionPdf(workspaceId, docId, versionId, filename) {
  const res = await rrFetch(`/api/rehearsal-room/work-documents/${docId}/versions/${versionId}/export.pdf?wrk_id=${workspaceId}`);
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const err = new Error((body && (body.detail?.message || body.detail)) || "export_failed");
    err.status = res.status;
    throw err;
  }
  const blob = await res.blob();
  triggerBlobDownload(blob, filename || "document.pdf");
}

// -- Lease + block save: called directly (not via useMutation) by
// useBlockLease so it retains full control of error branching (locked vs
// conflict vs denied) without React Query's mutation-state indirection.
export async function acquireBlockLease(workspaceId, docId, blockId) {
  return request(`/api/rehearsal-room/work-documents/${docId}/blocks/${blockId}/lease?wrk_id=${workspaceId}`, { method: "POST" });
}

export async function heartbeatBlockLease(workspaceId, docId, blockId, leaseId, leaseToken) {
  return request(`/api/rehearsal-room/work-documents/${docId}/blocks/${blockId}/lease/${leaseId}/heartbeat?wrk_id=${workspaceId}`, {
    method: "POST", body: JSON.stringify({ lease_token: leaseToken }),
  });
}

export async function releaseBlockLease(workspaceId, docId, blockId, leaseId, leaseToken) {
  return request(`/api/rehearsal-room/work-documents/${docId}/blocks/${blockId}/lease/${leaseId}/release?wrk_id=${workspaceId}`, {
    method: "POST", body: JSON.stringify({ lease_token: leaseToken }),
  });
}

export async function saveBlockContent(workspaceId, docId, blockId, { content, expectedBlockRevision, leaseId, leaseToken, idempotencyKey }) {
  return jsonPatch(`/api/rehearsal-room/work-documents/${docId}/blocks/${blockId}?wrk_id=${workspaceId}`, {
    content, expected_block_revision: expectedBlockRevision, lease_id: leaseId, lease_token: leaseToken, idempotency_key: idempotencyKey,
  });
}

export async function fetchPresenceSnapshot(workspaceId, docId) {
  return request(`/api/rehearsal-room/work-documents/${docId}/presence/snapshot?wrk_id=${workspaceId}`);
}
